// src/components/ImageWithRetry.js
import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { CardMedia, CircularProgress, Box } from '@mui/material';

const ImageWithRetry = ({ 
  src, 
  alt, 
  maxRetries = 3,
  onError,
  fallbackImageUrl = 'https://via.placeholder.com/150?text=Image+Error',
  ...props 
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Hardcoded backend URL
  const BACKEND_URL = 'https://memorylink.onrender.com';
  
  // Ensure URLs are absolute
  const getAbsoluteUrl = useCallback((url) => {
    if (!url) return fallbackImageUrl;
    
    // If it's already an absolute URL
    if (url.startsWith('http')) {
      return url;
    }
    
    // Make sure the path starts with a slash
    const path = url.startsWith('/') ? url : `/${url}`;
    return `${BACKEND_URL}${path}`;
  }, [fallbackImageUrl]);
  
  // Set initial source as absolute URL
  const [currentSrc, setCurrentSrc] = useState(getAbsoluteUrl(src));

  const handleError = useCallback((e) => {
    console.error(`Image load error (attempt ${retryCount + 1}/${maxRetries}):`, currentSrc);
    
    if (retryCount < maxRetries) {
      setRetryCount(prev => prev + 1);
      
      // Try with a cache-busting parameter
      const originalSrc = getAbsoluteUrl(src);
      const cacheBuster = `retry=${Date.now()}`;
      const newSrc = originalSrc.includes('?') 
        ? `${originalSrc}&${cacheBuster}` 
        : `${originalSrc}?${cacheBuster}`;
      
      console.log(`Retrying with: ${newSrc}`);
      setCurrentSrc(newSrc);
      
      // Log debug info
      console.log('Image path details:', {
        original: src,
        absoluteUrl: originalSrc,
        retryUrl: newSrc,
        isAbsoluteUrl: src.startsWith('http'),
        backendUrl: BACKEND_URL
      });
      
      setTimeout(() => {
        if (e.target) {
          e.target.src = newSrc;
        }
      }, 1000 * Math.pow(2, retryCount));
    } else {
      setError(true);
      setLoading(false);
      
      if (onError) {
        onError(e, src);
      }
    }
  }, [retryCount, maxRetries, src, currentSrc, onError, getAbsoluteUrl]);

  const handleLoad = useCallback(() => {
    console.log(`Image loaded successfully: ${currentSrc}`);
    setLoading(false);
    setError(false);
  }, [currentSrc]);

  useEffect(() => {
    // Reset state when source changes
    const absoluteSrc = getAbsoluteUrl(src);
    if (absoluteSrc !== currentSrc && !currentSrc.includes(src)) {
      setRetryCount(0);
      setError(false);
      setLoading(true);
      setCurrentSrc(absoluteSrc);
      console.log(`Image source changed to: ${absoluteSrc}`);
    }
    
    const img = new Image();
    img.src = currentSrc;
    img.onload = handleLoad;
    img.onerror = handleError;

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [currentSrc, src, handleLoad, handleError, getAbsoluteUrl]);

  return (
    <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
      {loading && (
        <Box sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(0, 0, 0, 0.05)'
        }}>
          <CircularProgress size={24} />
        </Box>
      )}
      <CardMedia
        component="img"
        image={error ? fallbackImageUrl : currentSrc}
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        {...props}
      />
    </Box>
  );
};

// Add PropTypes
ImageWithRetry.propTypes = {
  src: PropTypes.string.isRequired,
  alt: PropTypes.string.isRequired,
  maxRetries: PropTypes.number,
  onError: PropTypes.func,
  fallbackImageUrl: PropTypes.string
};

export default ImageWithRetry;